"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import {
	IconCalendar,
	IconMapPin,
	IconArrowRight,
	IconHeart,
} from "@/lib/tabler-icons";
import { SectionHeading } from "@/components/ui/editorial/SectionHeading";
import { Caption } from "@/components/ui/editorial/Caption";
import { Kicker } from "@/components/ui/editorial/Kicker";
import { conferences, upcomingEvents } from "../data/conferences";

const ConferencesSection = () => {
	return (
		<section className="p-4 xl:px-0 py-16" aria-label="Conferencias">
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="space-y-4"
				>
					<SectionHeading
						index="03"
						title={
							<>
								<em className="italic text-terracotta">Conferencias</em>
							</>
						}
						as="h2"
					/>
					<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
						Transformando realidades a través de la palabra. Conferencias y
						talleres que sensibilizan, educan y crean espacios de reflexión y
						cambio social.
					</p>
				</motion.header>

				{/* Conferences Grid */}
				<div className="space-y-20">
					{conferences.map((conference, index) => (
						<motion.div
							key={conference.id}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: index * 0.1 }}
							viewport={{ once: true }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start text-center lg:text-left"
						>
							{/* Conference Image */}
							<figure
								className={`${index % 2 === 0 ? "order-2 lg:order-1" : "order-2"}`}
							>
								<div className="rounded-xl overflow-hidden border border-border">
									<Image
										src={conference.image}
										alt={`Imagen de la conferencia: ${conference.title}`}
										width={500}
										height={400}
										className="object-cover w-full h-auto"
									/>
								</div>
								<Caption>{conference.subtitle}</Caption>
							</figure>

							{/* Conference Content */}
							<div
								className={`space-y-8 ${index % 2 === 0 ? "order-1 lg:order-2" : "order-1"}`}
							>
								<div className="space-y-3">
									<Kicker>{conference.subtitle}</Kicker>
									<h3 className="font-display font-semibold text-ink text-3xl md:text-4xl leading-tight">
										{conference.title}
									</h3>
									<p className="font-body text-ink text-lg leading-relaxed">
										{conference.description}
									</p>
								</div>

								{/* Features */}
								<div className="space-y-3">
									<h4 className="font-display font-semibold text-ink text-sm tracking-wide uppercase">
										Características
									</h4>
									<ul className="space-y-2 w-fit mx-auto lg:mx-0">
										{conference.features.map((feature, featureIndex) => (
											<li key={featureIndex} className="flex items-start gap-3">
												<span className="text-terracotta mt-1.5 shrink-0 text-xs">—</span>
												<span className="font-body text-ink text-base">{feature}</span>
											</li>
										))}
									</ul>
								</div>

								{/* Topics */}
								<div className="space-y-3">
									<h4 className="font-display font-semibold text-ink text-sm tracking-wide uppercase">
										Temas abordados
									</h4>
									<div className="flex flex-wrap justify-center lg:justify-start gap-2">
										{conference.topics.map((topic, topicIndex) => (
											<span
												key={topicIndex}
												className="font-body text-ink text-sm border border-border px-3 py-1 rounded-full"
											>
												{topic}
											</span>
										))}
									</div>
								</div>
							</div>
						</motion.div>
					))}
				</div>

				{/* Upcoming Events */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="mt-20 space-y-8"
				>
					<div className="border-t border-border pt-12 space-y-2">
						<h3 className="font-display font-semibold text-ink text-2xl md:text-3xl">
							Próximos Eventos
						</h3>
						<p className="font-body text-muted-foreground text-base">
							Fechas señaladas donde podrás encontrarme
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{upcomingEvents.map((event, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.08 }}
								viewport={{ once: true }}
								className="border border-border rounded-xl p-6 space-y-3 hover:border-terracotta transition-colors duration-200"
							>
								<div className="flex items-center gap-3">
									<IconCalendar className="w-5 h-5 text-terracotta shrink-0" />
									<div className="flex items-center gap-2">
										<span className="font-display text-terracotta text-sm font-semibold">
											{event.date}
										</span>
										<span className="font-body text-muted-foreground text-sm">
											{event.time}
										</span>
									</div>
								</div>
								<h4 className="font-display font-semibold text-ink text-base leading-snug">
									{event.title}
								</h4>
								<div className="flex items-center gap-2 text-muted-foreground">
									<IconMapPin className="w-4 h-4 shrink-0" />
									<span className="font-body text-sm">{event.location}</span>
								</div>
							</motion.div>
						))}
					</div>
				</motion.div>

				{/* Call to Action */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="mt-20 border-t border-border pt-12 space-y-6"
				>
					<div className="space-y-2">
						<Kicker>¿Interesado en una conferencia?</Kicker>
						<h3 className="font-display font-semibold text-ink text-2xl md:text-3xl">
							Contratar una conferencia o taller
						</h3>
						<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
							Adapto mis conferencias y talleres según las necesidades de tu
							organización, centro educativo o evento. Cada presentación es
							única y diseñada para crear impacto.
						</p>
					</div>
					<div className="flex flex-col sm:flex-row gap-4">
						<Link
							href="/asociacion-equilibrio#contacto"
							className="inline-flex items-center justify-center gap-2 font-display text-sm bg-ink text-cream px-6 py-3 rounded-lg hover:bg-ink/90 transition-colors duration-200"
						>
							<IconHeart className="w-4 h-4" />
							Solicitar información
							<IconArrowRight className="w-4 h-4" />
						</Link>
						<Link
							href="/asociacion-equilibrio"
							className="inline-flex items-center justify-center gap-2 font-display text-sm border border-border text-ink px-6 py-3 rounded-lg hover:border-terracotta hover:text-terracotta transition-colors duration-200"
						>
							Ver proyectos de la Asociación
						</Link>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default ConferencesSection;
